import * as React from 'react'

import { action, computed, makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'

import { SitePermitStatus } from '~/client/graph'
import { SourceEntityType } from '~/client/graph/types.generated'
import { IAppConfig } from '~/client/src/shared/Config'
import IFilePreviewProperties from '~/client/src/shared/interfaces/IFilePreviewProperties'
import ClosuresStore from '~/client/src/shared/stores/domain/Closures.store'
import MarkupsStore from '~/client/src/shared/stores/domain/Markups.store'
import SitemapItemsStore from '~/client/src/shared/stores/domain/SitemapItems.store'
import SitemapsStore from '~/client/src/shared/stores/domain/Sitemaps.store'
import WorkflowsStore from '~/client/src/shared/stores/domain/Workflows.store'

import Localization from '../../localization/LocalizationManager'
import { ICalendarDates } from '../../models/CalendarEvent'
import Workflow from '../../models/Workflow'
import CompaniesStore from '../../stores/domain/Companies.store'
import { FileUploadingStore } from '../../stores/domain/FileUploading.store'
import GraphExecutorStore from '../../stores/domain/GraphExecutor.store'
import LocationAttributesStore from '../../stores/domain/LocationAttributes.store'
import MaterialCategoriesStore from '../../stores/domain/MaterialCategories.store'
import MaterialsStore from '../../stores/domain/Materials.store'
import MessagesStore from '../../stores/domain/MessagesStore/Messages.store'
import OperationRulesStore from '../../stores/domain/OperationRules.store'
import PermitInspectionChangesStore from '../../stores/domain/PermitInspectionChanges.store'
import PermitInspectionsStore from '../../stores/domain/PermitInspections.store'
import PermitStatusChangesStore from '../../stores/domain/PermitStatusChanges.store'
import PhotosStore from '../../stores/domain/Photos.store'
import ProjectMembersStore from '../../stores/domain/ProjectMembers.store'
import RecurringWorkflowSettingsStore from '../../stores/domain/RecurringWorkflowSettings.store'
import SitePermitFollowingsStore from '../../stores/domain/SitePermitFollowings.store'
import TagsStore from '../../stores/domain/Tags.store'
import ThreadsStore from '../../stores/domain/ThreadsStore/Threads.store'
import UserProjectsStore from '../../stores/domain/UserProjects.store'
import WorkflowTypesStore from '../../stores/domain/WorkflowTypes.store'
import EventsStore from '../../stores/EventStore/Events.store'
import InitialState from '../../stores/InitialState'
import PermitBallInCourtStore from '../../stores/ui/PermitBallInCourt.store'
import ProjectDateStore from '../../stores/ui/ProjectDate.store'
import CalendarPlaceholderStore from '../CalendarDayView/components/CalendarPlaceholder.store'
import PermitCompactHeaderBar from '../CompactHeaderBar/components/PermitCompactHeaderBar'
import EntityDateMoveConfirm from '../EntityDateMoveConfirm/EntityDateMoveConfirm'
import FileInputBase from '../FileInput/FileInput'
import FutureIntervalsRestrictionsDialog from '../FutureIntervalsRestrictionsDialog/FutureIntervalsRestrictionsDialog'
import PermitLogActionBarStore from '../PermitLogActionBar.store'
import BicRuleChoiceDialog from './BicRuleChoiceDialog/BicRuleChoiceDialog'
import FormDeleteConfirm from './components/FormDeleteConfirm'
import PermitActionBar from './components/PermitActionBar/PermitActionBar'
import PermitFormLoader from './components/PermitFormLoader'
import WorkflowMoreMenu from './components/WorkflowMoreMenu'
import FormReportGenerationStore from './FormReportGeneration.store'
import PermitApprovalStore from './PermitApproval.store'
import SitePermitCreationFormContent from './SitePermitCreationFormContent'
import SitePermitCreationFormStore, {
  FormDetailsTab,
} from './WorkflowCreationForm.store'

import './SitePermitCreationForm.scss'

interface IProps {
  permitToShow: Workflow
  isMobileMode?: boolean
  calendarEventDates?: ICalendarDates
  isDateMoveConfirmModalOpen?: boolean
  FileInputType: typeof FileInputBase

  close(): void
  onDuplicateClick(workflow: Workflow): void
  openFilePreview?: (
    files: IFilePreviewProperties[],
    currentIndex: number,
  ) => void
  hideDateMoveConfirmModal?: () => void
  openWorkflow?: (workflow: Workflow) => void
  projectDateStore?: ProjectDateStore
  eventsStore?: EventsStore
  fileUploadingStore?: FileUploadingStore
  companiesStore?: CompaniesStore
  workflowTypesStore?: WorkflowTypesStore
  workflowsStore?: WorkflowsStore
  operationRulesStore?: OperationRulesStore
  permitStatusChangesStore?: PermitStatusChangesStore
  permitInspectionsStore?: PermitInspectionsStore
  permitInspectionChangesStore?: PermitInspectionChangesStore
  threadsStore?: ThreadsStore
  messagesStore?: MessagesStore
  photosStore?: PhotosStore
  userProjectsStore?: UserProjectsStore
  projectMembersStore?: ProjectMembersStore
  tagsStore?: TagsStore
  calendarPlaceholderStore?: CalendarPlaceholderStore
  locationAttributesStore?: LocationAttributesStore
  materialsStore?: MaterialsStore
  materialCategoryStore?: MaterialCategoriesStore
  workflowFollowingsStore?: SitePermitFollowingsStore
  configuration?: IAppConfig
  sitemapItemsStore?: SitemapItemsStore
  sitemapsStore?: SitemapsStore
  graphExecutorStore?: GraphExecutorStore
  recurringWorkflowSettingsStore?: RecurringWorkflowSettingsStore
  closuresStore?: ClosuresStore
  markupsStore?: MarkupsStore
}

@inject(
  'projectDateStore',
  'eventsStore',
  'fileUploadingStore',
  'companiesStore',
  'workflowTypesStore',
  'workflowsStore',
  'operationRulesStore',
  'permitStatusChangesStore',
  'threadsStore',
  'photosStore',
  'messagesStore',
  'permitInspectionsStore',
  'permitInspectionChangesStore',
  'userProjectsStore',
  'projectMembersStore',
  'tagsStore',
  'locationAttributesStore',
  'materialsStore',
  'materialCategoryStore',
  'calendarPlaceholderStore',
  'workflowFollowingsStore',
  'configuration',
  'sitemapItemsStore',
  'sitemapsStore',
  'graphExecutorStore',
  'recurringWorkflowSettingsStore',
  'sitemapItemsStore',
  'closuresStore',
  'markupsStore',
)
@observer
export default class SitePermitViewForm extends React.Component<IProps> {
  @observable private localProps: IProps = null
  private readonly store: SitePermitCreationFormStore
  private readonly permitApprovalStore: PermitApprovalStore
  private readonly permitBicStore: PermitBallInCourtStore
  private readonly actionBarStore: PermitLogActionBarStore
  private readonly formReportGenerationStore: FormReportGenerationStore

  @observable private isStatusSelectorOpened: boolean = false
  @observable private isEditMode: boolean = false
  @computed
  private get isFormReadOnly(): boolean {
    const { existingPermit, isFormEditAllowedForUser } = this.store
    return (
      existingPermit.isDeleted ||
      existingPermit.isDoneOrDenied ||
      !(
        isFormEditAllowedForUser ||
        this.canChangeStatus ||
        this.store.isBicUserForCurrentStep
      )
    )
  }

  public constructor(props: IProps) {
    super(props)

    this.localProps = props
    this.store = new SitePermitCreationFormStore(
      props.eventsStore,
      props.projectDateStore,
      props.fileUploadingStore,
      props.workflowTypesStore,
      props.userProjectsStore,
      props.projectMembersStore,
      props.companiesStore,
      props.locationAttributesStore,
      props.materialsStore,
      props.materialCategoryStore,
      props.tagsStore,
      props.graphExecutorStore,
      props.closuresStore,
      props.markupsStore,
      props.workflowsStore,
      props.permitStatusChangesStore,
      props.permitInspectionsStore,
      props.permitInspectionChangesStore,
      props.calendarPlaceholderStore,
      props.recurringWorkflowSettingsStore,
      props.sitemapItemsStore,
    )

    this.store.setDefaultSitePermit(props.permitToShow)
    this.store.setOpenWorkflowHandler(props.openWorkflow)

    this.formReportGenerationStore = new FormReportGenerationStore(
      props.projectDateStore,
      props.eventsStore,
      props.configuration,
      props.sitemapItemsStore,
      props.sitemapsStore,
      props.locationAttributesStore,
      props.projectMembersStore,
      props.workflowTypesStore,
      props.companiesStore,
      props.materialsStore,
      props.userProjectsStore,
    )

    this.permitBicStore = new PermitBallInCourtStore(
      props.eventsStore,
      props.operationRulesStore,
      props.projectMembersStore,
      props.workflowTypesStore,
      props.companiesStore,
    )
    this.store.setPermitBicStore(this.permitBicStore)

    this.actionBarStore = new PermitLogActionBarStore(
      props.eventsStore,
      props.threadsStore,
      props.messagesStore,
      props.photosStore,
      props.fileUploadingStore,
      props.userProjectsStore,
      props.tagsStore,
      props.workflowFollowingsStore,
      {
        sourceEntityId: props.permitToShow.id,
        sourceEntityType: SourceEntityType.Form,
        locationIds: props.permitToShow.locations.map(l => l.id),
        locationName:
          props.locationAttributesStore.getLocationBySiteLocationObj(
            props.permitToShow.locations[0],
          )?.name,
        projectId: props.permitToShow.projectId,
        sourceEntityCode: props.permitToShow.code,
        sourceEntityTypeName: props.permitToShow.getNameOfPermitType(
          props.workflowTypesStore,
        ),
      },
    )

    this.permitApprovalStore = new PermitApprovalStore(
      this.store,
      this.actionBarStore,
      props.close,
      props.eventsStore,
      this.permitBicStore,
    )

    this.store.loadFormLogData()
    makeObservable(this)
  }

  public componentDidUpdate(prevProps: IProps) {
    const { permitToShow } = this.props

    if (permitToShow !== prevProps.permitToShow) {
      const {
        inspectionFieldsStore,
        setDefaultSitePermit,
        scrollToBottom,
        hideNonWorkTimeLabel,
        unblockSubmitButton,
        markPermitAsNonWorkTimeIfNeed,
        resetSelectedField,
        workflowFieldsStore,
      } = this.store

      inspectionFieldsStore.deselectInspection()
      unblockSubmitButton()
      hideNonWorkTimeLabel()

      resetSelectedField()
      setDefaultSitePermit(permitToShow)

      markPermitAsNonWorkTimeIfNeed()
      workflowFieldsStore.expandAllSections()

      this.disableEditMode()

      Promise.resolve().then(() => scrollToBottom())
    }

    if (permitToShow.id !== prevProps.permitToShow.id) {
      this.store.clearFormLogData()
      this.store.loadFormLogData()
    }
  }

  public componentWillUnmount(): void {
    this.store.clearFormLogData()
  }

  public render() {
    const {
      close,
      openWorkflow,
      workflowTypesStore,
      FileInputType,
      projectDateStore,
      isDateMoveConfirmModalOpen,
      isMobileMode,
    } = this.props

    const {
      editablePermit,
      existingPermit,
      template,
      isDeleteConfirmModalOpen,
      toggleDeleteConfirmModal,
      deleteForm,
    } = this.store

    return (
      <div className="site-permit-creation-form full-height relative">
        <div className="site-permit-creation-form-content full-height col">
          <PermitCompactHeaderBar
            permit={existingPermit}
            workflowTypesStore={workflowTypesStore}
            onCloseClick={close}
            menuEl={this.workflowMoreMenu}
            openWorkflow={openWorkflow}
          />
          <div className="full-height col overflow-hidden relative">
            <FormDeleteConfirm
              isShown={isDeleteConfirmModalOpen}
              isRecurringWorkflow={!!editablePermit?.recurringSettingsId}
              onHide={toggleDeleteConfirmModal}
              onApply={thisAndFuture => deleteForm(thisAndFuture, close)}
            />
            <FutureIntervalsRestrictionsDialog
              isShown={this.store.isFutureIntervalsRestrictionsWarningShown}
              intervals={this.store.futureInvalidIntervals}
              onHide={this.store.closeFutureIntervalsRestrictionsWarning}
              onApply={this.store.closeFutureIntervalsRestrictionsWarning}
              formatInterval={(start, end) =>
                this.localProps.projectDateStore.getFullDateIntervalToDisplay(
                  start,
                  end,
                )
              }
            />
            <EntityDateMoveConfirm
              isShown={!this.isFormReadOnly && isDateMoveConfirmModalOpen}
              title={Localization.translator.updateForm}
              onHide={this.hideMovePermitDatesModal}
              onApply={this.modePermitDates}
            />
            <PermitFormLoader
              permitCreationStore={this.store}
              permitActionBarStore={this.actionBarStore}
            />
            <SitePermitCreationFormContent
              store={this.store}
              isReadonly={this.isFormReadOnly}
              FileInputType={FileInputType}
              isViewMode={this.isViewMode}
              toggleMode={this.toggleEditMode}
              onPermitClose={close}
              permitLogActionBarStore={this.actionBarStore}
              permitBicStore={this.permitBicStore}
            />
            {!!template && !existingPermit.isDeleted && (
              <PermitActionBar
                isStatusSelectorOpened={this.isStatusSelectorOpened}
                isActionBarInReadOnlyMode={this.isActionBarReadOnly}
                canChangeStatus={this.canChangeStatus}
                toggleStatusSelectMenu={this.toggleStatusSelectMenu}
                applyStatusAndStep={this.applyStatusAndStep}
                state={this.appState}
                store={this.store}
                permitApprovalStore={this.permitApprovalStore}
                actionBarStore={this.actionBarStore}
                projectDateStore={projectDateStore}
                isMobileMode={isMobileMode}
              />
            )}
            <BicRuleChoiceDialog store={this.permitApprovalStore} />
          </div>
        </div>
      </div>
    )
  }

  private get workflowMoreMenu(): JSX.Element {
    const { onDuplicateClick, workflowsStore } = this.props

    return (
      <WorkflowMoreMenu
        store={this.store}
        isDisabled={this.store.isLoading || workflowsStore?.isDeleting}
        isMobileMode={this.props.isMobileMode}
        onStatusChange={this.applyStatusAndStep}
        onOpenStatusSelector={this.toggleStatusSelectMenu}
        onReportSubmitClick={
          this.formReportGenerationStore.generateAndSendReport
        }
        onDuplicateClick={onDuplicateClick}
      />
    )
  }

  // Shared by the header menu's Update Status submenu and the ActionBar's
  // status selector, so both apply a status the same way.
  @action.bound
  private applyStatusAndStep(status: SitePermitStatus, stepId: string) {
    const { isPermitValid, editablePermit, updatePermit, switchActiveTab } =
      this.store

    if (!isPermitValid) return

    // The selector's rows are plain buttons, so nothing dismisses it for us.
    this.isStatusSelectorOpened = false

    editablePermit.setStatus(status)
    if (stepId) {
      editablePermit.setWorkflowStepId(stepId)
    }

    updatePermit(
      editablePermit.isChanged ? this.actionBarStore.draftMessage : null,
    )
    switchActiveTab(FormDetailsTab.LOG)
  }

  @action.bound
  private toggleStatusSelectMenu() {
    this.isStatusSelectorOpened = !this.isStatusSelectorOpened
  }

  private hideMovePermitDatesModal = () => {
    this.props.hideDateMoveConfirmModal()
    this.props.close()
  }

  private modePermitDates = () => {
    this.store.applyDates(
      this.props.calendarEventDates.startDate,
      this.props.calendarEventDates.endDate,
    )

    this.props.hideDateMoveConfirmModal()
  }

  @computed
  private get isActionBarReadOnly(): boolean {
    return (
      !this.appState.userActiveProjectSettings.isAdminOrFormsMaster &&
      this.isFormReadOnly
    )
  }

  private get isViewMode(): boolean {
    const { isRequestedOrSubmitted, isChanged } = this.store.existingPermit
    return !this.isEditMode && !isRequestedOrSubmitted && !isChanged
  }

  @action
  private toggleEditMode = (event: React.MouseEvent<HTMLButtonElement>) => {
    this.isEditMode = !this.isEditMode
    event.stopPropagation()
  }

  @action.bound
  private disableEditMode() {
    this.isEditMode = false
  }

  private get appState(): InitialState {
    return this.localProps.eventsStore.appState
  }

  @computed
  private get canChangeStatus(): boolean {
    const { existingPermit, template } = this.store
    if (!template) return false

    const {
      isCreator,
      isRequesterOrResponsible,
      isApprovalStep,
      isReviewStep,
    } = existingPermit
    const { user, userActiveProjectSettings } = this.appState

    if (userActiveProjectSettings.isAdminOrFormsMaster) return true

    if (this.store.isBicUserForCurrentStep) return true

    const isLimitedStep = isReviewStep(template) || isApprovalStep(template)

    return (
      !isLimitedStep &&
      (isCreator(user.id) || isRequesterOrResponsible(user.id))
    )
  }
}
